import {
  CHECK_POPUP_REQUEST_SIGN_EMPLOYMENT_CONTRACT,
  EApiStatus,
} from 'constants/common';
import { EMemberCenterErrorCode } from 'constants/memberCenter';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/routeConfig';
import { useSnackbar } from 'hooks/index';
import axios from 'plugins/api/axios';
import cookie, { KeyCookie } from 'plugins/cookie/cookie';
import { ILoginBody, IUnverifiedAccountInfo, TLoginContext } from 'types/login';
import { t as trans } from 'utils/i18n';
import { TeacherRecruitmentDto } from 'src/types/teacherRecruitment';
import {
  IDataExistReferenceSystem,
  IPayloadCreateReferenceSystem,
  IPayloadUpdate,
  UserType,
} from 'types/referenceSystem';
import { ICreateRecommendationCode } from 'types/recommendationCode';
import { resolveTeacherAccount } from './resolveTeacherAccount';

const { teacherRecruit, referenceSystem, recommendationCode, memberCenter } =
  axios;
const UnVerified = '信箱不存在';
const Verified = '信箱已存在';
export const LoginContext = createContext<TLoginContext>({
  isLogin: false,
  setIsLogin: () => false,
  login: async () => {},
  logout: async () => {},
  username: '',
  dataCheckEmailExist: '',
  unverifiedAccountInfo: null,
  clearUnverifiedAccount: () => {},
});

export enum ThrowCode {
  Failed_GenCode = 'Failed_GenCode',
  Failed_Create_TeacherRecruitment = 'Failed_Create_TeacherRecruitment',
  Failed_Create_ReferenceSystem = 'Failed_Create_ReferenceSystem',
  Failed_Update_ReferralCode_TeacherRecruitment = 'Failed_Update_ReferralCode_TeacherRecruitment',
  Failed_Update_ReferralCode_ReferenceSystem = 'Failed_Update_ReferralCode_ReferenceSystem',
}

interface ICreateAccountReferenceSystemWithTypeOneClubID {
  jwt: string;
  dataLogin: ILoginBody;
  dataTeacherRecruit: TeacherRecruitmentDto;
}
interface IHandleAccountReferenceSystemTypeOneClubID {
  jwt: string;
  dataReferenceSystem: IDataExistReferenceSystem;
  dataTeacherRecruit: TeacherRecruitmentDto;
}

export const LoginProvider: FC = ({ children }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [dataCheckEmailExist, setDataCheckEmailExist] = useState<string>('');
  const [unverifiedAccountInfo, setUnverifiedAccountInfo] =
    useState<IUnverifiedAccountInfo | null>(null);
  const clearUnverifiedAccount = () => {
    setDataCheckEmailExist('');
    setUnverifiedAccountInfo(null);
  };
  /* Sign out */
  const logout = async () => {
    const token = cookie.get(KeyCookie.newTeacherClient);
    const oneClassToken = cookie.get(KeyCookie.oneClassClient) as
      | { code: string; jwt: string }
      | undefined;
    if (oneClassToken?.jwt?.includes(token)) {
      cookie.remove(KeyCookie.oneClassClient);
    }
    cookie.remove(KeyCookie.newTeacherClient);
    if (
      localStorage.getItem(
        CHECK_POPUP_REQUEST_SIGN_EMPLOYMENT_CONTRACT.IS_DISPLAYED,
      )
    ) {
      localStorage.removeItem(
        CHECK_POPUP_REQUEST_SIGN_EMPLOYMENT_CONTRACT.IS_DISPLAYED,
      );
    }
    setIsLogin(false);
    navigate(PATH.LOGIN, { replace: true });
  };

  const removeToken = () => {
    const token = cookie.get(KeyCookie.newTeacherClient);
    const oneClassToken = cookie.get(KeyCookie.oneClassClient) as
      | { code: string; jwt: string }
      | undefined;
    if (oneClassToken?.jwt?.includes(token)) {
      cookie.remove(KeyCookie.oneClassClient);
    }
    cookie.remove(KeyCookie.newTeacherClient);
    setIsLogin(false);
  };

  const getReferralCodeAfterGen = async (
    payloadCreateCode: ICreateRecommendationCode,
  ) => {
    const {
      data: dataRecommendationCode,
      err: errRecommendationCode,
      msg: msgRecommendationCode,
    } = await recommendationCode.createCode(payloadCreateCode);

    if (!dataRecommendationCode && errRecommendationCode) {
      enqueueSnackbar(msgRecommendationCode, { variant: 'error' });
      throw new Error(ThrowCode.Failed_GenCode);
    }

    return dataRecommendationCode;
  };

  const createAccountReferenceSystemWithReferralCodeAndOneClubIdType = async (
    payloadCreateReferenceSystem: IPayloadCreateReferenceSystem,
  ) => {
    const {
      data: dataCreateReferenceSystem,
      error: errorCreateReferenceSystem,
    } = await referenceSystem.create(payloadCreateReferenceSystem);

    if (!dataCreateReferenceSystem && errorCreateReferenceSystem) {
      enqueueSnackbar(errorCreateReferenceSystem.message, { variant: 'error' });
      throw new Error(ThrowCode.Failed_Create_ReferenceSystem);
    }
  };
  const updateReferralCodeInTeacherRecruitment = async (
    invitationCode: string,
  ) => {
    const payloadUpdateTeacherRecruitment = {
      referralCode: invitationCode,
    } as TeacherRecruitmentDto;

    const {
      data: dataUpdateTeacherRecruitment,
      error: errorUpdateTeacherRecruitment,
      status: statusUpdateTeacherRecruitment,
    } = await teacherRecruit.update(payloadUpdateTeacherRecruitment);

    if (
      statusUpdateTeacherRecruitment !== EApiStatus.SUCCESS &&
      !dataUpdateTeacherRecruitment
    ) {
      enqueueSnackbar(errorUpdateTeacherRecruitment?.message, {
        variant: 'error',
      });
      throw new Error(ThrowCode.Failed_Update_ReferralCode_TeacherRecruitment);
    }
  };
  const updateReferralCodeInReferenceSystem = async (
    invitationCode: string,
    IDRecord: string,
  ) => {
    const payloadUpdateReferenceSystem = {
      referralCode: invitationCode,
    } as IPayloadUpdate;

    const {
      data: dataUpdateReferenceSystem,
      error: errorUpdateReferenceSystem,
    } = await referenceSystem.update(IDRecord, payloadUpdateReferenceSystem);

    if (!dataUpdateReferenceSystem) {
      enqueueSnackbar(errorUpdateReferenceSystem?.message, {
        variant: 'error',
      });
      throw new Error(ThrowCode.Failed_Update_ReferralCode_ReferenceSystem);
    }
  };

  const handleCreateAccountReferenceSystemWithTypeOneClubID = async (
    dataToCreateAccount: ICreateAccountReferenceSystemWithTypeOneClubID,
  ) => {
    const payloadCreateCode = {
      jwt: dataToCreateAccount.jwt,
    } as ICreateRecommendationCode;

    const dataRecommendationCode = await getReferralCodeAfterGen(
      payloadCreateCode,
    );

    const payloadCreateReferenceSystem = {
      userType: UserType.ONECLUB_ID,
      userId: dataToCreateAccount.dataLogin.username,
      referralCode: dataRecommendationCode?.invitationCode,
      email: dataToCreateAccount.dataTeacherRecruit.email,
    } as IPayloadCreateReferenceSystem;

    await createAccountReferenceSystemWithReferralCodeAndOneClubIdType(
      payloadCreateReferenceSystem,
    );

    await updateReferralCodeInTeacherRecruitment(
      dataRecommendationCode?.invitationCode as string,
    );
  };

  const handleAccountReferenceSystemWithTypeOneClubID = async (
    dataToHandle: IHandleAccountReferenceSystemTypeOneClubID,
  ) => {
    const payloadCreateCode = {
      jwt: dataToHandle.jwt,
    } as ICreateRecommendationCode;

    const dataReferenceSystem = dataToHandle.dataReferenceSystem;
    const dataTeacherRecruitment = dataToHandle.dataTeacherRecruit;

    if (
      dataReferenceSystem?.referralCode &&
      !dataTeacherRecruitment?.referralCode
    ) {
      await updateReferralCodeInTeacherRecruitment(
        dataReferenceSystem.referralCode,
      );
    } else {
      const dataRecommendationCode = await getReferralCodeAfterGen(
        payloadCreateCode,
      );

      await updateReferralCodeInTeacherRecruitment(
        dataRecommendationCode?.invitationCode as string,
      );

      await updateReferralCodeInReferenceSystem(
        dataRecommendationCode?.invitationCode as string,
        dataReferenceSystem.id,
      );
    }
  };

  /* login */
  const login = async (data: ILoginBody) => {
    // Obtain Member Center token
    const signinRes = await memberCenter.signin({
      account: data.username,
      password: data.password,
    });

    if (signinRes.err !== EMemberCenterErrorCode.SUCCESS || !signinRes.data) {
      enqueueSnackbar(
        signinRes.msg || trans('login.signinFailed', '帳號或密碼錯誤'),
        { variant: 'error' },
      );
      setIsLogin(false);
      return;
    }

    const userName = data.username;
    const accessToken = signinRes.data.accessToken;
    setUsername(userName);

    cookie.set(KeyCookie.newTeacherClient, accessToken);

    const oneClassToken = cookie.get(KeyCookie.oneClassClient) as
      | { code: string; jwt: string }
      | undefined;
    if (!oneClassToken) {
      cookie.setNaniOneClassCookie(
        KeyCookie.oneClassClient,
        JSON.stringify({ code: 'SUCCESS', jwt: accessToken }),
      );
    }

    // #region check verify email
    // 這段要在打教師資料 API 之前檢查：信箱未驗證時，teachers.getMe()/
    // teacherRecruit.get() 後端會直接回 errorCode 1005 擋掉查詢，如果先判斷教師
    // 資料存不存在，會誤判成「帳號資料異常」而蓋掉真正該顯示的未驗證彈窗。
    const deviceVerified = signinRes.data.deviceVerified;

    if (!deviceVerified) {
      enqueueSnackbar(
        trans('login.noAccountData', '帳號資料異常，請聯繫客服'),
        { variant: 'error' },
      );
      removeToken();
      return;
    }

    if (!deviceVerified.email) {
      setDataCheckEmailExist(UnVerified);
      setUnverifiedAccountInfo({
        account: userName,
        email: deviceVerified.emailMask ?? '',
      });
      removeToken();
      return;
    }
    // #endregion check verify email

    const resolution = await resolveTeacherAccount();

    if (resolution.status === 'conflict') {
      removeToken();
      enqueueSnackbar(
        trans(
          'login.isStaffMemberOrCustomer',
          'This account already has another identity. Please use a different account to log in or register a new account',
        ),
        { variant: 'error' },
      );
      return;
    }

    if (resolution.status === 'accountDataAnomaly') {
      enqueueSnackbar(
        trans('login.noAccountData', '帳號資料異常，請聯繫客服'),
        { variant: 'error' },
      );
      removeToken();
      return;
    }

    if (resolution.status === 'backendError') {
      enqueueSnackbar(resolution.message, { variant: 'error' });
      removeToken();
      return;
    }

    const {
      teacherRecruitData,
      globalDataReferenceSystem,
      typeAccountFromReferenceSystem,
    } = resolution;

    setDataCheckEmailExist(Verified);

    // start main handle sync data between Teacher Recruitment and Reference System
    if (
      teacherRecruitData &&
      (!globalDataReferenceSystem.referralCode ||
        !teacherRecruitData.referralCode)
    ) {
      try {
        switch (typeAccountFromReferenceSystem) {
          case '': {
            const paramsCreateAccount = {
              jwt: accessToken,
              dataLogin: data,
              dataTeacherRecruit: teacherRecruitData,
            };
            await handleCreateAccountReferenceSystemWithTypeOneClubID(
              paramsCreateAccount as ICreateAccountReferenceSystemWithTypeOneClubID,
            );
            break;
          }

          case UserType.ONECLUB_ID: {
            const paramsHandleAccountTypeOneClubID = {
              jwt: accessToken,
              dataReferenceSystem: globalDataReferenceSystem,
              dataTeacherRecruit: teacherRecruitData,
            } as IHandleAccountReferenceSystemTypeOneClubID;
            await handleAccountReferenceSystemWithTypeOneClubID(
              paramsHandleAccountTypeOneClubID,
            );
            break;
          }
        }
      } catch (error) {
        window.console.error(error);
      }
    }
    // end main handle sync data between Teacher Recruitment and Reference System

    setIsLogin(true);
  };

  useEffect(() => {
    const existingToken = cookie.get(KeyCookie.newTeacherClient);
    if (existingToken) {
      setIsLogin(true);
    }
  }, []);

  return (
    <LoginContext.Provider
      value={{
        isLogin,
        setIsLogin,
        login,
        logout,
        username,
        dataCheckEmailExist,
        unverifiedAccountInfo,
        clearUnverifiedAccount,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginService = () => useContext<TLoginContext>(LoginContext);
